"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { apiClient } from "@/services/api";

export default function WechatLoginDialog({ open, onClose, onSuccess }) {
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus] = useState("pending");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 生成二维码
  const generateQR = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiClient.auth.wechatLogin();

      if (response.success) {
        setQrCodeUrl(response.data.qrCodeUrl);
        setSessionId(response.data.sessionId);
        setStatus("pending");
        startPolling(response.data.sessionId);
      } else {
        setError(response.message || "生成二维码失败");
      }
    } catch (error) {
      setError("网络错误，请重试");
    } finally {
      setLoading(false);
    }
  };

  // 轮询检查登录状态
  const startPolling = (sid) => {
    const interval = setInterval(async () => {
      try {
        const response = await apiClient.auth.wechatCheckStatus({ sessionId: sid });

        if (response.success && response.data.status === "success") {
          clearInterval(interval);
          setStatus("success");
          onSuccess(response.data.userInfo);
        } else if (response.data.status === "expired") {
          clearInterval(interval);
          setStatus("expired");
          setError("登录已过期，请重新扫码");
        } else if (response.data.status === "failed") {
          clearInterval(interval);
          setStatus("failed");
          setError("登录失败，该微信未绑定账号");
        }
      } catch (error) {
        console.error("检查登录状态失败:", error);
      }
    }, 2000); // 每2秒检查一次

    // 5分钟后停止轮询
    setTimeout(
      () => {
        clearInterval(interval);
        if (status === "pending") {
          setStatus("expired");
          setError("登录超时，请重新扫码");
        }
      },
      5 * 60 * 1000,
    );
  };

  useEffect(() => {
    if (open) {
      generateQR();
    } else {
      setQrCodeUrl("");
      setSessionId("");
      setStatus("pending");
      setError("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#07C160", fontWeight: "bold" }}
          >
            微信扫码登录
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ textAlign: "center", py: 2 }}>
          {loading ? (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} sx={{ color: "#07C160" }} />
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "text.secondary" }}
              >
                正在生成二维码...
              </Typography>
            </Box>
          ) : qrCodeUrl ? (
            <Box>
              <Box
                sx={{
                  display: "inline-block",
                  p: 2,
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  mb: 2,
                }}
              >
                <img
                  src={qrCodeUrl}
                  alt="微信登录二维码"
                  style={{
                    width: 250,
                    height: 250,
                    display: "block",
                  }}
                />
              </Box>

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                {status === "pending" && "请使用微信扫描二维码登录"}
                {status === "success" && "登录成功！正在跳转..."}
                {status === "expired" && "登录已过期，请重新扫码"}
                {status === "failed" &&
                  '登录失败，该微信未绑定账号。如已有账号，请登录后在"个人中心"绑定微信，如无账号，请先注册。'}
              </Typography>

              {status === "pending" && (
                <Typography variant="caption" color="text.secondary">
                  扫码登录前，请确保您的账号已绑定了微信账号，如无，请登录后在“个人中心”进行绑定操作。
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} sx={{ color: "#07C160" }} />
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "text.secondary" }}
              >
                正在生成二维码...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
